import type {Component, Editor} from 'grapesjs';
import {createRoot} from 'react-dom/client';
import React from 'react';
import {DescriptionRteColorAction} from './description-rte-color-action';
import {DescriptionRteEmojiAction} from './description-rte-emoji-action';
import {POSTERMYWALL_DESCRIPTION_TEXT_TYPE} from '@FormMakerReact/components/postermywall-description-text/postermywall-description-text';
import {noop} from '@Utils/general.util';

const registeredEditors = new WeakSet<Editor>();
const savedRangeByEditor = new WeakMap<Editor, Range>();
const savedInsertionOffsetByEditor = new WeakMap<Editor, number>();

export function getSavedDescriptionRange(editor: Editor): Range | undefined {
  return savedRangeByEditor.get(editor);
}

export function captureDescriptionInsertionPoint(editor: Editor): void {
  const selected = editor.getSelected();
  if (!isDescription(selected)) {
    return;
  }

  const descEl = selected?.getEl();
  const doc = descEl?.ownerDocument;
  const selection = doc?.getSelection();

  if (!descEl || !doc || !selection || selection.rangeCount === 0) {
    return;
  }

  const range = selection.getRangeAt(0);
  if (!descEl.contains(range.commonAncestorContainer)) {
    return;
  }

  savedInsertionOffsetByEditor.set(editor, getCharOffset(descEl, doc, range));
}

function getCharOffset(descEl: HTMLElement, doc: Document, range: Range): number {
  const measure = doc.createRange();
  measure.selectNodeContents(descEl);
  measure.setEnd(range.startContainer, range.startOffset);
  return measure.toString().length;
}

export function captureDescriptionSelection(editor: Editor): void {
  const selected = editor.getSelected();
  if (!isDescription(selected)) {
    return;
  }

  const descriptionEl = selected?.getEl();
  const doc = descriptionEl?.ownerDocument;
  const selection = doc?.getSelection();

  if (!descriptionEl || !selection || selection.rangeCount === 0) {
    return;
  }

  const range = selection.getRangeAt(0);

  if (!descriptionEl.contains(range.commonAncestorContainer) || range.startContainer.nodeType !== Node.TEXT_NODE) {
    return;
  }

  savedRangeByEditor.set(editor, range.cloneRange());
}

export function registerDescriptionRteActions(editor: Editor): void {
  editor.onReady((): void => {
    bindSelectionTracker(editor);
  });

  editor.on('rte:enable', (): void => {
    if (!registeredEditors.has(editor)) {
      registeredEditors.add(editor);
      installCustomActions(editor);
      shieldToolbarFromOutsideClickDetection(editor);
    }
    syncToolbarVisibility(editor);
  });

  editor.on('component:selected', (): void => {
    syncToolbarVisibility(editor);
  });
}

function shieldToolbarFromOutsideClickDetection(editor: Editor): void {
  const toolbar = editor.RichTextEditor.getToolbarEl();

  if (!toolbar) {
    return;
  }

  const stop = (event: Event): void => {
    event.stopPropagation();
  };

  toolbar.addEventListener('mousedown', stop);
  toolbar.addEventListener('click', stop);
}

function installCustomActions(editor: Editor): void {
  const rte = editor.RichTextEditor;
  rte.remove('bold');
  rte.remove('italic');
  rte.remove('underline');
  rte.remove('strikethrough');
  rte.remove('link');

  rte.add('color', {
    icon: mountReactIcon(editor, <DescriptionRteColorAction editor={editor} />),
    attributes: {title: window.i18next.t('pmwjs_color'), class: 'gjs-rte-action pmw-fm-rte-action'},
    result: noop,
  });

  rte.add('divider', {
    icon: '',
    attributes: {class: 'pmw-fm-rte-divider-wrap'},
    result: noop,
  });

  rte.add('bold', {
    icon: '<i class="pmw-icon icon-bold size-icon-16"></i>',
    attributes: {title: window.i18next.t('pmwjs_bold'), class: 'gjs-rte-action pmw-fm-rte-action'},
    result: (rteInstance): void => {
      rteInstance.exec('bold');
    },
  });

  rte.add('italic', {
    icon: '<i class="pmw-icon icon-italic size-icon-16"></i>',
    attributes: {title: window.i18next.t('pmwjs_italic'), class: 'gjs-rte-action pmw-fm-rte-action'},
    result: (rteInstance): void => {
      rteInstance.exec('italic');
    },
  });

  rte.add('underline', {
    icon: '<i class="pmw-icon icon-underline size-icon-16"></i>',
    attributes: {title: window.i18next.t('pmwjs_underline'), class: 'gjs-rte-action pmw-fm-rte-action'},
    result: (rteInstance): void => {
      rteInstance.exec('underline');
    },
  });

  rte.add('strikethrough', {
    icon: '<i class="pmw-icon icon-strike-through size-icon-16"></i>',
    attributes: {title: window.i18next.t('pmwjs_strikethrough'), class: 'gjs-rte-action pmw-fm-rte-action'},
    result: (rteInstance): void => {
      rteInstance.exec('strikethrough');
    },
  });

  rte.add('emoji', {
    icon: mountReactIcon(
      editor,
      <DescriptionRteEmojiAction
        onOpen={(): void => {
          captureDescriptionInsertionPoint(editor);
        }}
        onSelect={(emoji: string): void => {
          insertEmojiIntoSelection(editor, emoji);
        }}
      />
    ),
    attributes: {title: window.i18next.t('pmwjs_emoji'), class: 'gjs-rte-action pmw-fm-rte-action'},
    result: noop,
  });
}

function mountReactIcon(editor: Editor, node: React.ReactNode): HTMLElement {
  const mountNode = document.createElement('div');
  const toolbar = editor.RichTextEditor.getToolbarEl();

  whenConnected(mountNode, toolbar, (): void => {
    createRoot(mountNode).render(node);
  });

  return mountNode;
}

function whenConnected(node: HTMLElement, scope: HTMLElement | null, callback: () => void): void {
  if (node.isConnected) {
    callback();
    return;
  }

  const observer = new MutationObserver((): void => {
    if (node.isConnected) {
      observer.disconnect();
      callback();
    }
  });

  observer.observe(scope ?? document.body, {childList: true, subtree: true});
}

function syncToolbarVisibility(editor: Editor): void {
  const toolbar = editor.RichTextEditor.getToolbarEl();
  if (!toolbar) {
    return;
  }

  toolbar.style.display = isDescription(editor.getSelected()) ? '' : 'none';
}

function isDescription(component: Component | undefined): boolean {
  return component?.get('type') === POSTERMYWALL_DESCRIPTION_TEXT_TYPE;
}

function bindSelectionTracker(editor: Editor): void {
  const doc = editor.Canvas.getBody()?.ownerDocument;
  if (!doc) {
    return;
  }

  doc.addEventListener('selectionchange', (): void => {
    if (isDescription(editor.getSelected())) {
      captureDescriptionSelection(editor);
    }
  });
}

function insertEmojiIntoSelection(editor: Editor, emoji: string): void {
  const doc = editor.Canvas.getBody()?.ownerDocument;
  const descEl = editor.getSelected()?.getEl();

  if (!doc || !descEl) {
    return;
  }

  const range = resolveInsertionRange(editor, descEl, doc);
  const selection = doc.getSelection();

  if (!range || !selection) {
    return;
  }

  doc.defaultView?.focus();
  descEl.focus({preventScroll: true});
  selection.removeAllRanges();
  selection.addRange(range);
  range.deleteContents();
  const textNode = doc.createTextNode(emoji);
  range.insertNode(textNode);
  range.setStartAfter(textNode);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
  savedRangeByEditor.set(editor, range.cloneRange());
  savedInsertionOffsetByEditor.delete(editor);
  descEl.dispatchEvent(new Event('input', {bubbles: true}));
  editor.getSelected()?.components(descEl.innerHTML);
}

function resolveInsertionRange(editor: Editor, descEl: HTMLElement, doc: Document): Range {
  const offset = savedInsertionOffsetByEditor.get(editor);
  const offsetRange = offset === undefined ? null : rangeFromCharOffset(descEl, doc, offset);

  if (offsetRange) {
    return offsetRange;
  }

  const range = doc.createRange();
  const lastTextNode = findLastTextNode(descEl);

  if (lastTextNode) {
    range.setStart(lastTextNode, lastTextNode.textContent?.length ?? 0);
    range.collapse(true);
    return range;
  }

  range.selectNodeContents(descEl);
  range.collapse(false);

  return range;
}

function rangeFromCharOffset(descEl: HTMLElement, doc: Document, offset: number): Range | null {
  const walker = doc.createTreeWalker(descEl, NodeFilter.SHOW_TEXT);
  let remaining = offset;
  let lastTextNode: Text | null = null;
  let node = walker.nextNode();

  while (node) {
    lastTextNode = node as Text;
    const length = node.textContent?.length ?? 0;

    if (remaining <= length) {
      const range = doc.createRange();
      range.setStart(node, remaining);
      range.collapse(true);

      return range;
    }

    remaining -= length;
    node = walker.nextNode();
  }

  if (lastTextNode) {
    const range = doc.createRange();
    range.setStart(lastTextNode, lastTextNode.textContent?.length ?? 0);
    range.collapse(true);

    return range;
  }

  return null;
}

function findLastTextNode(root: HTMLElement): Text | null {
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);

  let last: Text | null = null;
  let node = walker.nextNode();

  while (node) {
    last = node as Text;
    node = walker.nextNode();
  }

  return last;
}
